import { getDocument } from 'pdfjs-dist';
import './configurePdfjsWorker';

const INVOICE_PDF_CANVAS_BRIGHTNESS = 0.86;
const PDF_EXPORT_RASTER_SCALE = 2;

async function readPdfBuffer(source: string | File | ArrayBuffer): Promise<ArrayBuffer> {
    if (source instanceof ArrayBuffer) return source;
    if (source instanceof File) return source.arrayBuffer();
    const res = await fetch(source, { mode: 'cors' });
    if (!res.ok) throw new Error('Could not download the invoice PDF.');
    return res.arrayBuffer();
}

// pdfjs `PDFPageProxy` render typings are stricter than our canvas-only usage.
async function renderPdfPagesToFile(pages: any[], outName: string): Promise<File> {
    const { jsPDF } = await import('jspdf');
    const exportCanvas = document.createElement('canvas');
    const exportCtx = exportCanvas.getContext('2d');
    if (!exportCtx) throw new Error('Canvas is not available.');

    let pdfOut: InstanceType<typeof jsPDF> | null = null;
    for (const page of pages) {
        const vp = page.getViewport({ scale: PDF_EXPORT_RASTER_SCALE }) as { width: number; height: number };
        const w = Math.floor(vp.width);
        const h = Math.floor(vp.height);
        exportCanvas.width = w;
        exportCanvas.height = h;
        exportCtx.setTransform(1, 0, 0, 1, 0, 0);
        exportCtx.fillStyle = '#ffffff';
        exportCtx.fillRect(0, 0, w, h);
        exportCtx.filter = `brightness(${INVOICE_PDF_CANVAS_BRIGHTNESS})`;
        const task = page.render({
            canvasContext: exportCtx,
            viewport: vp,
        });
        await (task as { promise: Promise<void> }).promise;
        exportCtx.filter = 'none';

        const imgData = exportCanvas.toDataURL('image/jpeg', 0.88);
        if (!pdfOut) {
            pdfOut = new jsPDF({ unit: 'pt', format: [w, h], compress: true });
            pdfOut.addImage(imgData, 'JPEG', 0, 0, w, h);
        } else {
            pdfOut.addPage([w, h]);
            pdfOut.addImage(imgData, 'JPEG', 0, 0, w, h);
        }
    }
    if (!pdfOut) throw new Error('Failed to build PDF.');
    const outBlob = pdfOut.output('blob');
    return new File([outBlob], outName, { type: 'application/pdf' });
}

/**
 * Build a new PDF file that includes only the given 1-based page numbers (in order).
 */
export async function buildPdfSubsetFromSource(
    source: string | File | ArrayBuffer,
    pages1Based: number[]
): Promise<File> {
    if (pages1Based.length === 0) {
        throw new Error('At least one page is required.');
    }
    const buf = await readPdfBuffer(source);
    const doc = await getDocument({ data: buf }).promise;
    const pageObjs: Awaited<ReturnType<typeof doc.getPage>>[] = [];
    for (const p of pages1Based) {
        if (p < 1 || p > doc.numPages) {
            throw new Error(`Invalid page ${p} (document has ${doc.numPages} page(s)).`);
        }
        pageObjs.push(await doc.getPage(p));
    }
    return renderPdfPagesToFile(pageObjs, 'invoice-pages.pdf');
}

/** Concatenate all pages from `base` followed by all pages from `extra` into one PDF file. */
export async function mergePdfFiles(base: File, extra: File): Promise<File> {
    const aBuf = await base.arrayBuffer();
    const bBuf = await extra.arrayBuffer();
    const docA = await getDocument({ data: aBuf }).promise;
    const docB = await getDocument({ data: bBuf }).promise;
    const pageObjs: Awaited<ReturnType<typeof docA.getPage>>[] = [];
    for (let p = 1; p <= docA.numPages; p += 1) {
        pageObjs.push(await docA.getPage(p));
    }
    for (let p = 1; p <= docB.numPages; p += 1) {
        pageObjs.push(await docB.getPage(p));
    }
    return renderPdfPagesToFile(pageObjs, 'invoice-merged.pdf');
}

function arrayMoveOrder<T>(arr: T[], from: number, to: number): T[] {
    const next = [...arr];
    const [x] = next.splice(from, 1);
    next.splice(to, 0, x);
    return next;
}

/** Reorder pages in a PDF file (0-based indices). */
export async function reorderPdfPagesInFile(file: File, fromIndex0: number, toIndex0: number): Promise<File> {
    const buf = await file.arrayBuffer();
    const doc = await getDocument({ data: buf }).promise;
    const n = doc.numPages;
    const order = Array.from({ length: n }, (_, i) => i + 1);
    const newOrder = arrayMoveOrder(order, fromIndex0, toIndex0);
    // Use the original File for subset generation; reusing `buf` after pdf.js
    // parsing can hit detached ArrayBuffer errors in some runtimes.
    return buildPdfSubsetFromSource(file, newOrder);
}

/** Remove one page (0-based). Returns null if that would leave zero pages. */
export async function removePdfPageAtIndex0(file: File, index0: number): Promise<File | null> {
    const buf = await file.arrayBuffer();
    const doc = await getDocument({ data: buf }).promise;
    const n = doc.numPages;
    const keep: number[] = [];
    for (let i = 0; i < n; i += 1) {
        if (i !== index0) keep.push(i + 1);
    }
    if (keep.length === 0) return null;
    // Use the original File for subset generation; reusing `buf` after pdf.js
    // parsing can hit detached ArrayBuffer errors in some runtimes.
    return buildPdfSubsetFromSource(file, keep);
}

/** Page count for upload validation / UI (multi-page PDFs are supported). */
export async function getPdfPageCountFromFile(file: File): Promise<number> {
    const buf = await file.arrayBuffer();
    const doc = await getDocument({ data: buf }).promise;
    return Math.max(1, doc.numPages);
}

function sanitizePdfFilename(filenameBase: string): string {
    const safeBase = filenameBase.replace(/[/\\?%*:|"<>]/g, '-').trim() || 'invoice';
    return safeBase.toLowerCase().endsWith('.pdf') ? safeBase : `${safeBase}.pdf`;
}

function triggerBrowserDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

/**
 * Download the original invoice PDF bytes (preserves vector/high-res quality).
 */
export async function downloadPdfFromSource(
    source: string | File | ArrayBuffer,
    filenameBase: string
): Promise<void> {
    const name = sanitizePdfFilename(filenameBase);
    let blob: Blob;
    if (source instanceof File) {
        blob = source.type === 'application/pdf' ? source : new Blob([await source.arrayBuffer()], { type: 'application/pdf' });
    } else if (source instanceof ArrayBuffer) {
        blob = new Blob([source], { type: 'application/pdf' });
    } else if (source.startsWith('data:')) {
        const res = await fetch(source);
        blob = await res.blob();
    } else {
        const res = await fetch(source, { mode: 'cors' });
        if (!res.ok) throw new Error('Could not download the invoice PDF.');
        blob = await res.blob();
    }
    triggerBrowserDownload(blob, name);
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        // Presigned S3 URLs need CORS so we can re-encode to PDF on canvas.
        if (!src.startsWith('data:')) {
            img.crossOrigin = 'anonymous';
        }
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Could not load invoice image for PDF export.'));
        img.src = src;
    });
}

/**
 * Build and download a multi-page PDF from raster invoice page URLs (high-res natural size).
 */
export async function downloadImagesAsPdf(imageSrcs: string[], filenameBase: string): Promise<void> {
    if (imageSrcs.length === 0) {
        throw new Error('No invoice images to download.');
    }
    const name = sanitizePdfFilename(filenameBase);
    const { jsPDF } = await import('jspdf');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas is not available.');

    let pdfOut: InstanceType<typeof jsPDF> | null = null;
    for (const src of imageSrcs) {
        const img = await loadImageElement(src);
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (w < 1 || h < 1) {
            throw new Error('Invoice image has invalid dimensions.');
        }
        canvas.width = w;
        canvas.height = h;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0);
        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        if (!pdfOut) {
            pdfOut = new jsPDF({ unit: 'pt', format: [w, h], compress: true });
            pdfOut.addImage(imgData, 'JPEG', 0, 0, w, h);
        } else {
            pdfOut.addPage([w, h]);
            pdfOut.addImage(imgData, 'JPEG', 0, 0, w, h);
        }
    }
    if (!pdfOut) throw new Error('Failed to build PDF.');
    pdfOut.save(name);
}

/** Low-res JPEG data URLs for each page (for thumbnails). */
export async function renderPdfPageDataUrlThumbnails(
    source: string | File | ArrayBuffer,
    maxEdgePx = 112
): Promise<string[]> {
    const buf = await readPdfBuffer(source);
    const doc = await getDocument({ data: buf }).promise;
    const out: string[] = [];
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas is not available.');
    for (let p = 1; p <= doc.numPages; p += 1) {
        const page = await doc.getPage(p);
        const baseVp = page.getViewport({ scale: 1 }) as { width: number; height: number };
        const scale = Math.min(maxEdgePx / baseVp.width, maxEdgePx / baseVp.height, 1);
        const vp = page.getViewport({ scale });
        const w = Math.floor(vp.width);
        const h = Math.floor(vp.height);
        canvas.width = w;
        canvas.height = h;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.filter = `brightness(${INVOICE_PDF_CANVAS_BRIGHTNESS})`;
        const task = page.render({
            canvasContext: ctx,
            viewport: vp,
        });
        await (task as { promise: Promise<void> }).promise;
        ctx.filter = 'none';
        out.push(canvas.toDataURL('image/jpeg', 0.82));
    }
    return out;
}
